<template>
  <div class="admin-page">
    <div class="admin-header">
      <h1>Pointe Criteria</h1>
      <router-link to="/pointe/admin/criteria" class="back-link">← Admin Home</router-link>
    </div>

    <div class="admin-actions">
      <button class="btn-primary" @click="openAddDialog">+ Add Criterion</button>
    </div>

    <div v-if="loading" class="loading-wrap">
      <v-progress-circular indeterminate color="#1a1a1a"></v-progress-circular>
    </div>

    <div v-else-if="criteria.length === 0" class="empty-state">
      <p>No criteria yet. Add your first criterion to get started.</p>
    </div>

    <div v-else class="criteria-list">
      <div
        v-for="(criterion, idx) in criteria"
        :key="criterion.id"
        class="criterion-row"
        :class="{ inactive: !criterion.active }"
      >
        <div class="criterion-order">
          <button class="order-btn" :disabled="idx === 0" @click="moveUp(idx)">▲</button>
          <span class="order-num">{{ idx + 1 }}</span>
          <button class="order-btn" :disabled="idx === criteria.length - 1" @click="moveDown(idx)">▼</button>
        </div>
        <div class="criterion-body">
          <div class="criterion-title">
            {{ criterion.title }}
            <span v-if="!criterion.active" class="badge-inactive">Inactive</span>
          </div>
          <div v-if="criterion.category" class="criterion-category">{{ criterion.category }}</div>
          <div v-if="criterion.description" class="criterion-description">{{ criterion.description }}</div>
          <div v-if="criterion.resourceLinks && criterion.resourceLinks.length" class="criterion-links">
            <span v-for="link in criterion.resourceLinks" :key="link.url" class="link-chip">
              <a :href="link.url" target="_blank">{{ link.label }}</a>
            </span>
          </div>
        </div>
        <div class="criterion-actions">
          <button class="btn-edit" @click="openEditDialog(criterion)">Edit</button>
          <button class="btn-toggle" @click="toggleActive(criterion)">
            {{ criterion.active ? 'Deactivate' : 'Activate' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Add/Edit Dialog -->
    <div v-if="dialogOpen" class="dialog-overlay" @click.self="closeDialog">
      <div class="dialog">
        <h2>{{ editingCriterion ? 'Edit Criterion' : 'Add Criterion' }}</h2>
        <form @submit.prevent="saveCriterion">
          <div class="form-group">
            <label>Title *</label>
            <input v-model="form.title" type="text" required class="form-input" />
          </div>
          <div class="form-group">
            <label>Category</label>
            <input v-model="form.category" type="text" class="form-input" placeholder="e.g. Foot, Strength, Alignment" />
          </div>
          <div class="form-group">
            <label>Description</label>
            <textarea v-model="form.description" class="form-input form-textarea" rows="3"></textarea>
          </div>
          <div class="form-group">
            <label>Resource Links</label>
            <div v-for="(link, i) in form.resourceLinks" :key="i" class="link-row">
              <input v-model="link.label" type="text" class="form-input link-label" placeholder="Label" />
              <input v-model="link.url" type="url" class="form-input link-url" placeholder="https://..." />
              <button type="button" class="btn-remove" @click="removeLink(i)">✕</button>
            </div>
            <button type="button" class="btn-add-link" @click="addLink">+ Add Link</button>
          </div>
          <div class="form-group">
            <label class="checkbox-label">
              <input type="checkbox" v-model="form.active" />
              Active (visible in applications and self-assessment)
            </label>
          </div>
          <p v-if="saveError" class="error-text">{{ saveError }}</p>
          <div class="dialog-actions">
            <button type="button" class="btn-secondary" @click="closeDialog">Cancel</button>
            <button type="submit" class="btn-primary" :disabled="saving">
              {{ saving ? 'Saving…' : 'Save' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { db } from '@/firebase'

export default {
  data () {
    return {
      criteria: [],
      loading: true,
      dialogOpen: false,
      editingCriterion: null,
      saving: false,
      saveError: '',
      form: {
        title: '',
        category: '',
        description: '',
        resourceLinks: [],
        active: true
      }
    }
  },
  async created () {
    await this.loadCriteria()
  },
  methods: {
    async loadCriteria () {
      this.loading = true
      const snap = await db.collection('pointe-criteria').orderBy('order').get()
      this.criteria = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }))
      this.loading = false
    },
    openAddDialog () {
      this.editingCriterion = null
      this.form = { title: '', category: '', description: '', resourceLinks: [], active: true }
      this.saveError = ''
      this.dialogOpen = true
    },
    openEditDialog (criterion) {
      this.editingCriterion = criterion
      this.form = {
        title: criterion.title || '',
        category: criterion.category || '',
        description: criterion.description || '',
        resourceLinks: (criterion.resourceLinks || []).map(l => ({ ...l })),
        active: criterion.active !== false
      }
      this.saveError = ''
      this.dialogOpen = true
    },
    closeDialog () {
      this.dialogOpen = false
      this.editingCriterion = null
    },
    addLink () {
      this.form.resourceLinks.push({ label: '', url: '' })
    },
    removeLink (i) {
      this.form.resourceLinks.splice(i, 1)
    },
    async saveCriterion () {
      this.saving = true
      this.saveError = ''
      try {
        const data = {
          title: this.form.title.trim(),
          category: this.form.category.trim(),
          description: this.form.description.trim(),
          resourceLinks: this.form.resourceLinks.filter(l => l.label && l.url),
          active: this.form.active
        }
        if (this.editingCriterion) {
          await db.collection('pointe-criteria').doc(this.editingCriterion.id).update(data)
        } else {
          const order = this.criteria.length
          await db.collection('pointe-criteria').add({ ...data, order, createdAt: new Date() })
        }
        await this.loadCriteria()
        this.closeDialog()
      } catch (e) {
        this.saveError = e.message
      }
      this.saving = false
    },
    async toggleActive (criterion) {
      await db.collection('pointe-criteria').doc(criterion.id).update({ active: !criterion.active })
      criterion.active = !criterion.active
    },
    async moveUp (idx) {
      if (idx === 0) return
      await this.swapOrder(idx, idx - 1)
    },
    async moveDown (idx) {
      if (idx === this.criteria.length - 1) return
      await this.swapOrder(idx, idx + 1)
    },
    async swapOrder (a, b) {
      const batch = db.batch()
      batch.update(db.collection('pointe-criteria').doc(this.criteria[a].id), { order: b })
      batch.update(db.collection('pointe-criteria').doc(this.criteria[b].id), { order: a })
      await batch.commit()
      await this.loadCriteria()
    }
  }
}
</script>

<style scoped>
.admin-page {
  max-width: 900px;
  margin: 0 auto;
  padding: 32px 24px;
  font-family: 'Fanwood Text', Georgia, serif;
}

.admin-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
}

.admin-header h1 {
  font-family: 'Quattrocento', Georgia, serif;
  font-size: 28px;
  color: #1a1a1a;
  margin: 0;
}

.back-link {
  font-size: 14px;
  color: #666;
  text-decoration: none;
}

.back-link:hover { color: #1a1a1a; }

.admin-actions { margin-bottom: 24px; }

.loading-wrap { padding: 40px; text-align: center; }

.empty-state { padding: 40px; text-align: center; color: #888; }

.criteria-list { display: flex; flex-direction: column; gap: 12px; }

.criterion-row {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 16px;
  border: 1px solid #e8e5e1;
  border-radius: 4px;
  background: #fff;
}

.criterion-row.inactive { opacity: 0.55; }

.criterion-order {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  min-width: 32px;
}

.order-btn {
  background: none;
  border: 1px solid #e8e5e1;
  border-radius: 2px;
  cursor: pointer;
  font-size: 10px;
  padding: 2px 6px;
  line-height: 1;
}

.order-btn:disabled { opacity: 0.3; cursor: default; }

.order-num { font-size: 12px; color: #888; }

.criterion-body { flex: 1; }

.criterion-title {
  font-size: 16px;
  font-weight: 600;
  color: #1a1a1a;
  margin-bottom: 4px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.badge-inactive {
  font-size: 11px;
  background: #f0f0f0;
  color: #888;
  padding: 2px 8px;
  border-radius: 20px;
}

.criterion-category { font-size: 12px; color: #999; margin-bottom: 4px; text-transform: uppercase; letter-spacing: .05em; }

.criterion-description { font-size: 14px; color: #555; line-height: 1.5; margin-bottom: 8px; }

.criterion-links { display: flex; gap: 8px; flex-wrap: wrap; }

.link-chip a {
  font-size: 12px;
  color: #1a1a1a;
  text-decoration: underline;
  background: #f5f4f2;
  padding: 3px 10px;
  border-radius: 20px;
}

.criterion-actions { display: flex; flex-direction: column; gap: 8px; min-width: 90px; }

.btn-primary {
  background: #1a1a1a;
  color: #fff;
  border: none;
  padding: 10px 20px;
  font-family: 'Fanwood Text', Georgia, serif;
  font-size: 14px;
  cursor: pointer;
  border-radius: 2px;
}

.btn-primary:hover { background: #333; }
.btn-primary:disabled { opacity: 0.5; cursor: default; }

.btn-secondary {
  background: #fff;
  color: #1a1a1a;
  border: 1px solid #1a1a1a;
  padding: 10px 20px;
  font-family: 'Fanwood Text', Georgia, serif;
  font-size: 14px;
  cursor: pointer;
  border-radius: 2px;
}

.btn-edit, .btn-toggle {
  background: none;
  border: 1px solid #e8e5e1;
  padding: 6px 12px;
  font-size: 13px;
  font-family: 'Fanwood Text', Georgia, serif;
  cursor: pointer;
  border-radius: 2px;
  color: #1a1a1a;
}

.btn-edit:hover, .btn-toggle:hover { border-color: #1a1a1a; }

/* Dialog */
.dialog-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 200;
  padding: 24px;
}

.dialog {
  background: #fff;
  border-radius: 4px;
  padding: 32px;
  max-width: 560px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
}

.dialog h2 {
  font-family: 'Quattrocento', Georgia, serif;
  font-size: 22px;
  margin: 0 0 24px;
  color: #1a1a1a;
}

.form-group { margin-bottom: 18px; }

.form-group label {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: #1a1a1a;
  margin-bottom: 6px;
  text-transform: uppercase;
  letter-spacing: .04em;
}

.form-input {
  width: 100%;
  border: 1px solid #e8e5e1;
  padding: 10px 12px;
  font-family: 'Fanwood Text', Georgia, serif;
  font-size: 15px;
  border-radius: 2px;
  box-sizing: border-box;
  color: #1a1a1a;
  background: #fff;
}

.form-input:focus { outline: none; border-color: #1a1a1a; }

.form-textarea { resize: vertical; }

.link-row { display: flex; gap: 8px; margin-bottom: 8px; align-items: center; }

.link-label { flex: 1; }
.link-url { flex: 2; }

.btn-remove {
  background: none;
  border: none;
  cursor: pointer;
  color: #999;
  font-size: 14px;
  padding: 4px 8px;
}

.btn-remove:hover { color: #c00; }

.btn-add-link {
  background: none;
  border: 1px dashed #ccc;
  padding: 6px 16px;
  font-family: 'Fanwood Text', Georgia, serif;
  font-size: 13px;
  cursor: pointer;
  border-radius: 2px;
  color: #666;
  margin-top: 4px;
}

.btn-add-link:hover { border-color: #1a1a1a; color: #1a1a1a; }

.checkbox-label {
  display: flex !important;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  text-transform: none !important;
  letter-spacing: 0 !important;
  font-size: 15px !important;
  font-weight: 400 !important;
}

.dialog-actions { display: flex; gap: 12px; justify-content: flex-end; margin-top: 24px; }

.error-text { color: #c00; font-size: 14px; margin-top: 8px; }
</style>
